// SCR-STAFF-994 — تیکت‌ها (US-CHN-040 تا 045؛ AP-CHN-02): صف تیکت‌های مرکز با فیلتر باز/وضعیت/نوع/مسئول/از مهلت گذشته، وضعیت SLA (هشدار ۸۰٪ و ارجاع) و ثبت تیکت
// داخلی یا از طرف مراجعه‌کننده. بدون chn.ticket.handle فقط تیکت‌های خود کاربر؛ شکایت بالینی فقط برای مجاز (BR-CHN-040). ثبت با ?new=1 (از پیام ورودی نامعلوم MSG
// با source و ref) دیالوگ را پیش‌پر باز می‌کند.
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { unwrap, type ChnTicketListItem, type ChnTicketSource, type ChnTicketStatus } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { WriteNotice } from '../settings/common';
import {
  categoryLabel,
  chnTicketKeys,
  CreateTicketDialog,
  SCR_STAFF_994,
  slaTone,
  statusTone,
  TICKET_STATUSES,
  TICKET_TYPES,
  ticketPath,
  ticketSubject,
  type TicketPrefill,
} from './ticketing';

type AssigneeFilter = '' | 'me' | 'unassigned';

export function TicketsPage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('chn.ticket.create');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState<ChnTicketStatus | ''>('');
  const [type, setType] = useState('');
  const [assignee, setAssignee] = useState<AssigneeFilter>('');
  const [breached, setBreached] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const prefill: TicketPrefill | null =
    params.get('new') === '1'
      ? {
          source: (params.get('source') as ChnTicketSource | null) ?? undefined,
          sourceRef: params.get('ref'),
          patient: params.get('patientId') ? { id: params.get('patientId') ?? '', displayName: params.get('patientName') ?? '' } : null,
        }
      : null;
  const [creating, setCreating] = useState(false);
  const handler = access.can('chn.ticket.handle');

  const tickets = useQuery({
    queryKey: [...chnTicketKeys.all(session?.tenantId), 'list', open, status, type, assignee, breached, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/channels/tickets', {
          params: { query: { open: open && !status, status: status || undefined, type: type || undefined, assignee: assignee || undefined, breached, cursor, limit: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<ChnTicketListItem>[] = [
    { id: 'number', headerKey: 'chn:tickets.columns.number', cell: (row) => <bdi>{faDigits(String(row.number))}</bdi> },
    {
      id: 'subject',
      headerKey: 'chn:tickets.columns.subject',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <span>{ticketSubject(t, row.subject, row.source)}</span>
          {row.sensitivity === 'Clinical' ? <Chip size="small" color="error" variant="outlined" label={t('chn:tickets.clinical')} /> : null}
        </Stack>
      ),
    },
    { id: 'category', headerKey: 'chn:tickets.columns.category', cell: (row) => `${t(`chn:tickets.type.${row.type}`)} · ${categoryLabel(t, row.category, row.categoryName)}` },
    { id: 'status', headerKey: 'chn:tickets.columns.status', cell: (row) => <StatusChip tone={statusTone(row.status)} labelKey={`chn:tickets.status.${row.status}`} /> },
    {
      id: 'sla',
      headerKey: 'chn:tickets.columns.sla',
      cell: (row) =>
        row.slaState === 'None' ? '—' : (
          <span data-testid={`chn-ticket-sla-${row.id}`} data-sla={row.slaState}>
            <StatusChip tone={slaTone(row.slaState)} labelKey={`chn:tickets.sla.${row.slaState}`} />
          </span>
        ),
    },
    { id: 'priority', headerKey: 'chn:tickets.columns.priority', cell: (row) => t(`chn:tickets.priority.${row.priority}`) },
    { id: 'assignee', headerKey: 'chn:tickets.columns.assignee', cell: (row) => row.assigneeName ?? (row.team ? t('chn:tickets.team', { team: t(`iam:role.${row.team}`, { defaultValue: row.team }) }) : '—') },
    { id: 'patient', headerKey: 'chn:tickets.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'source', headerKey: 'chn:tickets.columns.source', cell: (row) => t(`chn:tickets.source.${row.source}`) },
    { id: 'created', headerKey: 'chn:tickets.columns.created', cell: (row) => <JalaliDate value={row.createdAt} withTime /> },
    {
      id: 'open',
      headerKey: 'chn:tickets.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => navigate(ticketPath(row.id))} data-testid={`chn-ticket-open-${row.id}`}>
          {t('chn:tickets.open')}
        </Button>
      ),
    },
  ];

  const closeCreate = (created?: { ticket: { id: string } }) => {
    setCreating(false);
    if (prefill) {
      params.delete('new');
      setParams(params, { replace: true });
    }
    if (created) {
      void navigate(ticketPath(created.ticket.id));
    }
  };

  return (
    <ListPage
      screenId={SCR_STAFF_994}
      titleKey="chn:tickets.title"
      subtitleKey="chn:tickets.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="chn-ticket-new">
          {t('chn:tickets.new')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
            <FormControlLabel control={<Switch checked={open} onChange={(event) => { setCursor(undefined); setOpen(event.target.checked); }} />} label={t('chn:tickets.filters.open')} />
            <TextField select size="small" label={t('chn:tickets.filters.status')} value={status} onChange={(event) => { setCursor(undefined); setStatus(event.target.value as ChnTicketStatus | ''); }} sx={{ minWidth: 180 }}>
              <MenuItem value="">{t('chn:tickets.filters.all')}</MenuItem>
              {TICKET_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`chn:tickets.status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('chn:tickets.filters.type')} value={type} onChange={(event) => { setCursor(undefined); setType(event.target.value); }} sx={{ minWidth: 180 }}>
              <MenuItem value="">{t('chn:tickets.filters.all')}</MenuItem>
              {TICKET_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`chn:tickets.type.${value}`)}
                </MenuItem>
              ))}
              {access.can('chn.ticket.manage') ? <MenuItem value="PlatformSupport">{t('chn:tickets.type.PlatformSupport')}</MenuItem> : null}
            </TextField>
            {handler ? (
              <TextField select size="small" label={t('chn:tickets.filters.assignee')} value={assignee} onChange={(event) => { setCursor(undefined); setAssignee(event.target.value as AssigneeFilter); }} sx={{ minWidth: 160 }}>
                <MenuItem value="">{t('chn:tickets.filters.all')}</MenuItem>
                <MenuItem value="me">{t('chn:tickets.filters.assignee_me')}</MenuItem>
                <MenuItem value="unassigned">{t('chn:tickets.filters.assignee_unassigned')}</MenuItem>
              </TextField>
            ) : null}
            <FormControlLabel control={<Switch checked={breached} onChange={(event) => { setCursor(undefined); setBreached(event.target.checked); }} />} label={t('chn:tickets.filters.breached')} />
          </Stack>
        </Stack>
      }
    >
      <DataTable
        captionKey="chn:tickets.title"
        columns={columns}
        rows={tickets.data?.items}
        getRowId={(row) => row.id}
        isLoading={tickets.isPending}
        error={tickets.error}
        onRetry={() => void tickets.refetch()}
        hasMore={Boolean(tickets.data?.nextCursor)}
        onLoadMore={() => setCursor(tickets.data?.nextCursor ?? undefined)}
        emptyTitleKey="chn:tickets.empty"
      />
      {creating || (prefill && write.canWrite) ? <CreateTicketDialog prefill={prefill ?? undefined} onClose={closeCreate} /> : null}
    </ListPage>
  );
}
